import React, { memo, ReactElement, RefObject } from "react";
import { FlashList, ListRenderItem } from "@/components/atoms/FlashList";
import { RefreshControl } from "@/components/atoms/RefreshControl";

import AddToWatchlistActionsheet from "@/components/organisms/AddToWatchlistActionsheet";
import { CustomPromptRefProps } from "@/components/molecules/CustomPrompt/CustomPromptContainer";
import ItemSeparator from "@/components/molecules/ItemSeparator";
import PlayerDetailCenter from "@/components/organisms/PlayerDetailCenter";
import RightColumnContainer from "@/components/organisms/PlayerDetailRight";
import SelectionSortPopover from "@/components/organisms/SelectionSortPopover";
import { Stack } from "@/components/atoms/Stack";

import {
  PlayerDetailPlayerMetaFragment,
  PlayerSearchPlayerFragment,
  SimilarPlayersPlayerResultFragment,
} from "@/generated/graphql";
import { SwipeableProvider } from "@/providers/Swipeable";

/**
 * Props for the MainView component that renders the similar players list.
 */
export interface PlayerViewProps {
  bottomInset: number;
  loading: boolean;
  refreshing: boolean;
  isDark: boolean;
  showActionSheet: boolean;
  promptRef: RefObject<CustomPromptRefProps>;
  /**
   * Element rendered in the FlashList empty slot for the current read state
   * (SE-7596) -- computed by the Container from the discriminated
   * SimilarPlayersListState so a failed read can never land here as the
   * genuinely-empty "No Similar Players" copy. null for the stale/ready
   * states, which FlashList never asks the empty slot to fill.
   */
  listEmptyComponent: ReactElement | null;
  /**
   * Staleness banner shown above the rows when a cached read is on screen
   * but the last refresh failed (SE-7596). null outside that state.
   */
  staleBanner: ReactElement | null;
  similarPlayerResults: SimilarPlayersPlayerResultFragment[];
  playerDetail?: PlayerSearchPlayerFragment;
  /**
   * The route player, already fetched by the Container, for the centre pane
   * to show before a similar player is selected (SE-8573). undefined once one
   * is selected, so the pane fetches that player itself.
   */
  routePlayer?: PlayerDetailPlayerMetaFragment | null;
  /** Initial-load state of {@link routePlayer}; undefined once a similar player is selected. */
  routePlayerLoading?: boolean;
  isSmallScreen: boolean;
  onLoadMore: () => void;
  onRefresh: () => void;
  onAddPlayer: () => void;
  onClose: () => void;
  onAddWatchlistPress: () => void;
  onScrollBeginDrag: () => void;
  renderItem: ListRenderItem<SimilarPlayersPlayerResultFragment>;
  renderHeader: () => ReactElement | null;
  renderListFooterComponent: () => ReactElement | null;
}

/**
 * Extract player item key from similar player result
 * @param item - Similar player result fragment
 * @param index - Item index in list
 */
const keyExtractor = (
  item: SimilarPlayersPlayerResultFragment,
  index: number
) => `player-${item.player?.id}-${index}`;

/**
 * MainView component renders the similar players list with responsive layout.
 * @param root0 - Component props
 * @param root0.loading - Loading state for initial data fetch
 * @param root0.refreshing - Whether the list is being refreshed
 * @param root0.isDark - Whether dark theme is active
 * @param root0.listEmptyComponent - Element for the FlashList empty slot (SE-7596)
 * @param root0.staleBanner - Staleness banner shown above stale rows (SE-7596)
 * @param root0.similarPlayerResults - Array of similar player results to display
 * @param root0.bottomInset - Bottom safe area inset for proper spacing
 * @param root0.isSmallScreen - Whether the screen is mobile sized
 * @param root0.onLoadMore - Callback to load more results when end is reached
 * @param root0.onRefresh - Callback when user pulls to refresh
 * @param root0.onScrollBeginDrag - Callback when user begins scrolling
 * @param root0.renderItem - Function to render each player item
 * @param root0.renderHeader - Function to render the list header
 * @param root0.renderListFooterComponent - Function to render the list footer
 * @param root0.onAddPlayer - Callback when user adds a player to watchlist
 * @param root0.playerDetail - Currently selected player detail to display
 * @param root0.routePlayer - Route player the centre pane shows before a selection (SE-8573)
 * @param root0.routePlayerLoading - Initial-load state of the route player
 * @param root0.showActionSheet - Whether to show the add to watchlist action sheet
 * @param root0.onClose - Callback to close the action sheet
 * @param root0.onAddWatchlistPress - Callback when add to watchlist is pressed
 * @param root0.promptRef - Ref to the custom prompt component
 */
const MainView = ({
  loading,
  refreshing,
  isDark,
  listEmptyComponent,
  staleBanner,
  similarPlayerResults,
  bottomInset,
  isSmallScreen,
  onLoadMore,
  onRefresh,
  onScrollBeginDrag,
  renderItem,
  renderHeader,
  renderListFooterComponent,
  onAddPlayer,
  playerDetail,
  routePlayer,
  routePlayerLoading,
  showActionSheet,
  onClose,
  onAddWatchlistPress,
  promptRef,
}: PlayerViewProps) =>
  isSmallScreen ? (
    <SwipeableProvider>
      <Stack className="flex-1">
        {renderHeader()}
        {staleBanner}
        {(loading || !!similarPlayerResults.length) && (
          <Stack direction="horizontal" className="justify-end px-4 pb-2 pt-4">
            <SelectionSortPopover type="similarity" />
          </Stack>
        )}
        <FlashList
          data={similarPlayerResults}
          renderItem={renderItem}
          contentContainerClassName="flex-grow p-3 pt-4"
          contentContainerStyle={{ paddingBottom: bottomInset }}
          scrollEventThrottle={16}
          className="h-full flex-1"
          refreshing={refreshing}
          keyExtractor={keyExtractor}
          ListEmptyComponent={listEmptyComponent}
          ListFooterComponent={renderListFooterComponent}
          ItemSeparatorComponent={ItemSeparator}
          onEndReached={onLoadMore}
          onRefresh={onRefresh}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              colors={["white"]}
              tintColor="white"
            />
          }
          onScrollBeginDrag={onScrollBeginDrag}
        />
      </Stack>
    </SwipeableProvider>
  ) : (
    <Stack className="flex-1">
      <Stack direction="horizontal" className="flex-1">
        <Stack
          className={`h-full w-1/4 ${!isSmallScreen ? "border-r border-surface-muted p-4" : "border-r-4 border-surface-strong pr-2"}`}
        >
          {renderHeader()}
          {staleBanner}
          {(loading || !!similarPlayerResults.length) && (
            <SelectionSortPopover type="similarity" />
          )}
          <FlashList
            data={similarPlayerResults}
            renderItem={renderItem}
            contentContainerClassName="flex-grow h-full px-3"
            contentContainerStyle={{ paddingBottom: bottomInset }}
            scrollEventThrottle={16}
            refreshing={refreshing}
            className="h-full flex-1"
            showsVerticalScrollIndicator={false}
            keyExtractor={keyExtractor}
            ListEmptyComponent={listEmptyComponent}
            ListFooterComponent={renderListFooterComponent}
            ItemSeparatorComponent={ItemSeparator}
            onEndReached={onLoadMore}
            onRefresh={onRefresh}
            refreshControl={
              <RefreshControl
                refreshing={refreshing}
                onRefresh={onRefresh}
                colors={["white"]}
                tintColor="white"
              />
            }
            onScrollBeginDrag={onScrollBeginDrag}
          />
        </Stack>
        <PlayerDetailCenter
          playerId={playerDetail?.id}
          player={routePlayer}
          playerLoading={routePlayerLoading}
          onAddPlayer={onAddPlayer}
          isSimilarDetail
        />
        {playerDetail && (
          <RightColumnContainer type="note" playerId={playerDetail.id} />
        )}
        {showActionSheet && playerDetail && (
          <AddToWatchlistActionsheet
            item={playerDetail}
            showActionsheet={showActionSheet}
            onClose={onClose}
            onAddPress={onAddPlayer}
            onAddWatchlistPress={onAddWatchlistPress}
            loading={loading}
            isPlayerInAnyWatchlist={!!playerDetail?.isWatchedByCurrentUser}
            promptRef={promptRef}
            isDark={isDark}
          />
        )}
      </Stack>
    </Stack>
  );

MainView.displayName = "MainView";

export default memo(MainView);
